import Analytics from '@/utils/analytics';
import graphql from 'graphql-tag';
import i18n from '@/utils/i18n';
import MenuIcon from '@assets/icons/menu.svg';
import React, { useMemo, useState } from 'react';
import { ActionSheet } from '@/components/action-sheet';
import { ActivityIndicator } from '@/components/activity-indicator';
import { createStyles } from '@/contexts/theme-context';
import {
  CreditMemo_GetCreditMemoByIdQuery,
  CreditMemo_GetCreditMemoByIdQueryVariables,
} from '@/types';
import { CreditMemoDetailList } from '@/components/payments/credit-memo-detail-list';
import { CreditMemoPDFButton } from '@/components/credit-memo/credit-memo-pdf-button';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { formatCurrency, ORDER_ITEM_PRICE_FIELDS } from '@/utils/money';
import { formatDate } from '@/utils/date';
import { Props as NavigationHeaderContentProps } from '@/components/navigation-header-content';
import { OfflineComponent } from '@/components/offline-component';
import { PACK_FRAGMENT } from '@/contexts/items-context-queries';
import { PageContainer } from '@/components/page-container';
import { Text, View } from 'react-native';
import { useGetOpenCreditMemoPDF } from '@/hooks/use-get-open-credit-memo-pdf';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { usePaymentsContext } from '@/contexts/payments-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

const CREDIT_MEMO_LINE_ITEM_FIELDS = graphql`
  fragment CreditMemoLineItemFields on credit_memo_line_items {
    item_code
    item_name
    orders_item {
      ...OrderItemPriceFields
      orders_items_modifier_options {
        name
        uuid
      }
      pack {
        ...ItemsContext_PackFields
      }
      restaurant_display_name
      uuid
      variants_pack {
        external_item_id
        uuid
      }
    }
    pack_unit
    quantity
    unit_price_in_micros
    uuid
    variants_pack {
      external_item_id
      item {
        name
        uuid
      }
      pack {
        ...ItemsContext_PackFields
      }
      uuid
    }
  }

  ${ORDER_ITEM_PRICE_FIELDS}
  ${PACK_FRAGMENT}
`;

export const GET_CREDIT_MEMO_BY_ID = graphql`
  query CreditMemo_GetCreditMemoByID($creditMemoUUID: uuid!) {
    getCreditMemoById(credit_memo_id: $creditMemoUUID) {
      credit_memo {
        balance_in_micros
        credit_date
        credit_memo_line_items {
          ...CreditMemoLineItemFields
        }
        credit_number
        uuid
      }
      public_url
    }
  }
  ${CREDIT_MEMO_LINE_ITEM_FIELDS}
`;

interface Props {
  creditMemoUUID: string;
}

export const CreditMemoDetailComponent: React.FC<Props> = ({
  creditMemoUUID,
}) => {
  const { currencyCode } = usePaymentsContext();
  const { isSmallLayout } = useLayoutContext();
  const isOfflineModeActive = useOfflineModeActive();
  const [styles] = useStyles();

  const { data, loading, hasResponse } = useWrappedQuery<
    CreditMemo_GetCreditMemoByIdQuery,
    CreditMemo_GetCreditMemoByIdQueryVariables
  >(GET_CREDIT_MEMO_BY_ID, {
    fetchPolicy: 'cache-and-network',
    variables: {
      creditMemoUUID,
    },
  });

  const creditMemo = data?.getCreditMemoById.credit_memo;

  const [showMoreActionsSheet, setShowMoreActionsSheet] = useState(false);

  const openCreditMemoPDF = useGetOpenCreditMemoPDF(
    data?.getCreditMemoById.public_url,
    creditMemo?.uuid,
  );

  const totalPrice = useMemo(() => {
    if (!creditMemo) {
      return '';
    }

    const balance = creditMemo.balance_in_micros || 0;
    return formatCurrency(-balance, currencyCode);
  }, [creditMemo, currencyCode]);

  const moreActions = useMemo(() => {
    const actions = [];
    if (creditMemo) {
      actions.push(i18n.t('view_credit_memo_pdf'));
    }
    return actions;
  }, [creditMemo]);

  const navigationHeaderProps = useMemo((): NavigationHeaderContentProps => {
    if (loading || !creditMemo) {
      return {
        showBackButton: isSmallLayout,
        showShadow: false,
      };
    }

    return {
      actions:
        moreActions.length && isSmallLayout
          ? [
              {
                Icon: MenuIcon,
                iconProps: {
                  testID: 'credit-memo-menu-button',
                },
                onPress: () => {
                  Analytics.track('credit_memo.menu_button.press');
                  setShowMoreActionsSheet(true);
                },
              },
            ]
          : undefined,
      contentFooter: (
        <>
          <View style={styles.creditHeader}>
            <View style={styles.creditInfo}>
              <Text style={[TYPOGRAPHY.h2, styles.issuedOnDate]}>
                {i18n.t('issued_on_date', {
                  date: formatDate(creditMemo.credit_date),
                })}
              </Text>
              <Text style={[TYPOGRAPHY.medium, styles.creditSubtitleText]}>
                {i18n.t('credit_number', {
                  number: creditMemo.credit_number,
                })}
              </Text>
            </View>
            <View style={styles.navigationHeaderTitleLarge}>
              {!isSmallLayout ? (
                <View style={styles.creditMemoButtonRowTopLayout}>
                  <CreditMemoPDFButton
                    onPress={async () => await openCreditMemoPDF()}
                    trackingData={{
                      name: 'credit_memo.view_pdf_button',
                      properties: {
                        creditMemoUUID: creditMemo.uuid,
                      },
                    }}
                  />
                </View>
              ) : null}
            </View>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.totalStandard}>{i18n.t('total')}</Text>
            <Text style={styles.totalEmphasis}>{totalPrice}</Text>
          </View>
        </>
      ),
      showBackButton: isSmallLayout,
    };
  }, [
    creditMemo,
    isSmallLayout,
    loading,
    moreActions.length,
    openCreditMemoPDF,
    styles,
    totalPrice,
  ]);

  return (
    <PageContainer
      animateNavigationHeader
      navigationHeaderProps={navigationHeaderProps}
    >
      {(scrollViewProps) =>
        loading ? (
          <View style={styles.staticContentContainer}>
            <ActivityIndicator testID="loading-indicator" />
          </View>
        ) : !hasResponse && isOfflineModeActive ? (
          <View style={styles.staticContentContainer}>
            <OfflineComponent />
          </View>
        ) : (
          <>
            <CreditMemoDetailList
              creditMemoLineItems={creditMemo?.credit_memo_line_items || []}
              scrollViewProps={scrollViewProps}
            />
            {showMoreActionsSheet && moreActions.length && creditMemo ? (
              <ActionSheet
                actions={moreActions}
                isVisible
                onAction={async (_, index) => {
                  if (index === 0) {
                    await openCreditMemoPDF();
                  }
                  setShowMoreActionsSheet(false);
                }}
                onClose={() => setShowMoreActionsSheet(false)}
                primaryIndices={[0]}
              />
            ) : null}
          </>
        )
      }
    </PageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  creditHeader: {
    borderBottomColor: theme.colors.stroke,
    borderBottomWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  creditInfo: {
    paddingBottom: 8,
    paddingHorizontal: 8,
  },
  creditMemoButtonRowTopLayout: {
    flexDirection: 'row',
  },
  creditSubtitleText: {
    ...FONTS.primarySemiBold,
    color: theme.colors.inactiveText,
    textTransform: 'uppercase',
  },
  issuedOnDate: {
    marginBottom: 8,
  },
  navigationHeaderTitleLarge: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 8,
  },
  staticContentContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  totalEmphasis: {
    ...FONTS.primarySemiBold,
    fontSize: 22,
    lineHeight: 24,
  },
  totalRow: {
    backgroundColor: theme.colors.white,
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginHorizontal: 4,
    paddingHorizontal: 4,
    paddingVertical: 16,
  },
  totalStandard: {
    ...FONTS.primary,
    color: theme.colors.primary,
    fontSize: 22,
    lineHeight: 24,
  },
}));
